import { useState, useEffect, useRef } from "react";
import {
  Download,
  Loader2,
  ClipboardPaste,
  X,
  Music,
  Film,
  AlertCircle,
  ShieldCheck,
  Clock,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { analyzeUrl, api, formatApiError, track } from "@/lib/api";
import { useI18n } from "@/i18n";
import PlatformIcon from "./PlatformIcon";

function fmtSize(bytes) {
  if (!bytes) return null;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1000) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
}

export default function Downloader({ platform }) {
  const { t } = useI18n();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const analyzeRequestRef = useRef(0);

  useEffect(() => {
    const platformRequest = ++analyzeRequestRef.current;
    track({ type: "tool_view", platform_key: platform.key, tool_slug: `${platform.key}-downloader`, path: window.location.pathname });
    setUrl("");
    setResult(null);
    setError(null);
    return () => {
      if (analyzeRequestRef.current === platformRequest) analyzeRequestRef.current += 1;
    };
  }, [platform.key]);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setUrl(text.trim());
    } catch {
      toast.error("تعذّر الوصول إلى الحافظة");
    }
  };

  const handleAnalyze = async (e) => {
    e?.preventDefault();
    const trimmed = url.trim();
    const requestId = ++analyzeRequestRef.current;
    if (!trimmed) {
      setLoading(false);
      setResult(null);
      setError(t("invalid_url"));
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await analyzeUrl(platform.key, trimmed);
      if (requestId !== analyzeRequestRef.current) return;
      if (!data.qualities || data.qualities.length === 0) {
        setError(t("no_media"));
      } else {
        setResult(data);
      }
    } catch (err) {
      if (requestId !== analyzeRequestRef.current) return;
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      if (requestId === analyzeRequestRef.current) setLoading(false);
    }
  };

  const handleDownload = async (q) => {
    setDownloading(q.quality);
    try {
      const res = await api.get("/download", {
        params: { q: q.quality },
        headers: { "X-Download-Token": result.token },
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      const ext = q.type === "audio" ? "mp3" : "mp4";
      const base = (result.title || "aran-download").replace(/[^\w\u0600-\u06FF -]/g, "").slice(0, 50).trim() || "aran-download";
      a.download = `${base}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Let the browser consume the object URL before releasing it.
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success("تم بدء التحميل");
    } catch (err) {
      let msg = "تعذّر معالجة الرابط حاليًا. حاول مرة أخرى بعد قليل.";
      try {
        const txt = await err.response?.data?.text?.();
        if (txt) msg = formatApiError(JSON.parse(txt).detail);
      } catch {
        /* ignore */
      }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="w-full">
      {/* Input form */}
      <form onSubmit={handleAnalyze} className="aran-card p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Input
              data-testid="url-input-field"
              aria-label={`${t("paste_url_here")} — ${platform.name}`}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={`${t("paste_url_here")} — ${platform.name}`}
              dir="ltr"
              className="h-14 text-base bg-background/60 border-border pr-4 pl-24 rounded-xl text-left"
            />
            <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {url ? (
                <button
                  type="button"
                  data-testid="url-clear-button"
                  onClick={() => setUrl("")}
                  className="p-2 rounded-lg text-muted-foreground hover:bg-secondary transition-colors"
                  aria-label={t("clear")}
                >
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="url-paste-button"
                  onClick={handlePaste}
                  className="p-2 rounded-lg text-primary hover:bg-primary/10 transition-colors flex items-center gap-1 text-xs font-medium"
                  aria-label={t("paste")}
                >
                  <ClipboardPaste className="w-4 h-4" />
                  {t("paste")}
                </button>
              )}
            </div>
          </div>
          <Button
            type="submit"
            data-testid="analyze-submit-button"
            disabled={loading}
            className="h-14 px-8 text-base font-bold rounded-xl active:scale-95 transition-transform"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin ml-2" />
                {t("analyzing")}
              </>
            ) : (
              t("analyze")
            )}
          </Button>
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
          {t("privacy_note")}
        </p>
      </form>

      {/* Error */}
      {error && (
        <div
          data-testid="analyze-error"
          role="alert"
          className="mt-4 aran-card border-destructive/40 bg-destructive/10 p-4 flex items-start gap-3 animate-fade-up"
        >
          <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div className="text-sm text-destructive-foreground/90">{error}</div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="mt-6 aran-card p-5 flex flex-col sm:flex-row gap-5" role="status" aria-live="polite" aria-label={t("analyzing")}>
          <Skeleton className="w-full sm:w-56 h-40 rounded-xl bg-secondary" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-6 w-3/4 bg-secondary" />
            <Skeleton className="h-4 w-1/3 bg-secondary" />
            <div className="flex gap-2 pt-4">
              <Skeleton className="h-11 w-24 bg-secondary" />
              <Skeleton className="h-11 w-24 bg-secondary" />
              <Skeleton className="h-11 w-24 bg-secondary" />
            </div>
          </div>
        </div>
      )}

      {/* Result */}
      {result && !loading && (
        <div className="mt-6 aran-card p-5 animate-fade-up" data-testid="analyze-result">
          <div className="flex flex-col sm:flex-row gap-5">
            {result.thumbnail && (
              <div className="relative w-full sm:w-64 shrink-0">
                <img
                  src={result.thumbnail}
                  alt={result.title || platform.name}
                  data-testid="result-thumbnail"
                  className="w-full h-44 object-cover rounded-xl border border-border"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
                {result.duration_string && (
                  <span className="absolute bottom-2 left-2 bg-black/80 text-white text-xs px-2 py-0.5 rounded-md font-mono flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {result.duration_string}
                  </span>
                )}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-lg"
                  style={{ backgroundColor: `${platform.color}1a`, color: platform.color }}
                >
                  <PlatformIcon icon={platform.icon} className="w-4 h-4" />
                </span>
                <span className="text-xs text-muted-foreground">{platform.name}</span>
              </div>
              <h3 data-testid="result-title" className="font-head font-bold text-lg leading-snug line-clamp-2">
                {result.title}
              </h3>
              {result.uploader && (
                <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" />
                  {t("by")} {result.uploader}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <div className="text-sm font-semibold text-muted-foreground mb-3">
              {t("available_formats")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="quality-list">
              {result.qualities.map((q) => {
                const isAudio = q.type === "audio";
                const label = isAudio ? t("audio_mp3") : q.quality === "BEST" ? "أفضل جودة (MP4)" : q.quality;
                const busy = downloading === q.quality;
                return (
                  <button
                    key={q.quality}
                    data-testid={`download-format-${q.quality.toLowerCase()}`}
                    onClick={() => handleDownload(q)}
                    disabled={!!downloading}
                    className="group flex items-center justify-between gap-2 rounded-xl border border-border bg-background/40 hover:border-primary/50 hover:bg-primary/5 px-4 py-3 transition-all active:scale-95 disabled:opacity-60"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      {isAudio ? (
                        <Music className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Film className="w-4 h-4 text-primary" />
                      )}
                      <span className="flex flex-col items-start">
                        {label}
                        {q.filesize && (
                          <span className="text-[10px] text-muted-foreground font-normal">
                            {fmtSize(q.filesize)}
                          </span>
                        )}
                      </span>
                    </span>
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    ) : (
                      <Download className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
